import type { CustomFieldEntry, NodeType } from '../../entities/node';

const COMMON_FIELD_LIST = ['displayName', 'summary', 'tags', 'notes', 'customFields'];
const HUMAN_FIELD_LIST = ['occupationIds', 'projectIds', 'relatedHumanIds'];
const PROJECT_FIELD_LIST = ['participantHumanIds', 'relatedOccupationIds', 'relatedProjectIds'];
const OCCUPATION_FIELD_LIST = ['relatedHumanIds', 'typicalProjectIds'];

const COMMON_FIELDS = new Set(COMMON_FIELD_LIST);
const HUMAN_FIELDS = new Set(HUMAN_FIELD_LIST);
const PROJECT_FIELDS = new Set(PROJECT_FIELD_LIST);
const OCCUPATION_FIELDS = new Set(OCCUPATION_FIELD_LIST);

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function isFieldAllowed(nodeType: NodeType, key: string): boolean {
  if (COMMON_FIELDS.has(key)) {
    return true;
  }
  if (nodeType === 'Human') {
    return HUMAN_FIELDS.has(key);
  }
  if (nodeType === 'Project') {
    return PROJECT_FIELDS.has(key);
  }
  return OCCUPATION_FIELDS.has(key);
}

export function getAllowedAINodeFields(nodeType: NodeType): string[] {
  return [...COMMON_FIELD_LIST, ...getRelationFields(nodeType)];
}

export function validateAINodeFieldObject(
  nodeType: NodeType,
  fields: Record<string, unknown>,
  options: { pathPrefix?: string; requireDisplayName?: boolean } = {},
): string[] {
  const issues: string[] = [];
  const relationFields = getRelationFields(nodeType);

  for (const key of Object.keys(fields)) {
    if (!isFieldAllowed(nodeType, key)) {
      issues.push(`${formatPath(options.pathPrefix, key)} is not allowed for ${nodeType}.`);
    }
  }

  if (options.requireDisplayName && !('displayName' in fields)) {
    issues.push(`${formatPath(options.pathPrefix, 'displayName')} is required.`);
  }
  if ('displayName' in fields && (!isNonEmptyString(fields.displayName))) {
    issues.push(`${formatPath(options.pathPrefix, 'displayName')} must be a non-empty string.`);
  }
  if ('summary' in fields && typeof fields.summary !== 'string') {
    issues.push(`${formatPath(options.pathPrefix, 'summary')} must be a string.`);
  }
  if ('notes' in fields && typeof fields.notes !== 'string') {
    issues.push(`${formatPath(options.pathPrefix, 'notes')} must be a string.`);
  }
  if ('tags' in fields && !isStringArray(fields.tags)) {
    issues.push(`${formatPath(options.pathPrefix, 'tags')} must be an array of strings.`);
  }
  if ('customFields' in fields && !isValidCustomFields(fields.customFields)) {
    issues.push(`${formatPath(options.pathPrefix, 'customFields')} is invalid.`);
  }

  for (const relationField of relationFields) {
    if (relationField in fields && !isStringArray(fields[relationField])) {
      issues.push(`${formatPath(options.pathPrefix, relationField)} must be an array of strings.`);
    }
  }

  return issues;
}

function getRelationFields(nodeType: NodeType): string[] {
  if (nodeType === 'Human') {
    return [...HUMAN_FIELD_LIST];
  }
  if (nodeType === 'Project') {
    return [...PROJECT_FIELD_LIST];
  }
  return [...OCCUPATION_FIELD_LIST];
}

function formatPath(pathPrefix: string | undefined, key: string): string {
  return pathPrefix ? `${pathPrefix}.${key}` : key;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

function isValidCustomFields(value: unknown): value is CustomFieldEntry[] {
  return Array.isArray(value) && value.every((entry) => {
    const field = entry as Partial<CustomFieldEntry>;
    return typeof field.fieldId === 'string'
      && typeof field.label === 'string'
      && typeof field.value === 'string'
      && typeof field.valueType === 'string'
      && typeof field.displayOrder === 'number';
  });
}